import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { SearchablePicker } from '@/components/ui/SearchablePicker';
import { useCreateProperty, type CreatePropertyPayload } from '@/hooks/data/useProperties';
import { parseDeNumber } from '@/lib/parseDeNumber';
import { useClients } from '@/hooks/data/useClients';
import { BUILDING_TYPES, OBJECT_TYPES, VIENNA_DISTRICTS } from '@/lib/austria';
import { UserCircle2 } from 'lucide-react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Pre-selected, locked seller/landlord. When set, the deal-type switch
   * and the client picker are replaced by a read-only chip and the
   * property is linked to this client. Used when opening from a client's
   * detail view — works even for clients the audience-filtered picker
   * would NOT list (e.g. imported Abgeber leads), because the id is passed
   * directly. The deal type is derived from the client's type.
   */
  lockedSeller?: {
    id: string;
    label: string;
    secondary?: string;
    clientType: 'prodavac' | 'najmodavac';
  };
  /** Called with the new property id after a successful create. Lets the
   *  caller chain into e.g. booking an appointment for that property. */
  onCreated?: (propertyId: string) => void;
}

const EDI_REGEX = /^2483\/\d{5}$/;

const empty = (): CreatePropertyPayload => ({
  clientId: '',
  city: 'Wien',
  district: '',
  street: '',
  streetNumber: '',
  postalCode: '',
  entrance: '',
  floor: '',
  doorNumber: '',
  buildingType: '',
  objectType: '',
  roomsCount: '',
  ediRealNumber: '',
  kgGrundbuch: '',
  einlagezahl: '',
  bLaufnummer: '',
  marketingStage: 'lead',
});

export function CreatePropertyDialog({ open, onOpenChange, lockedSeller, onCreated }: Props) {
  const { t } = useTranslation();
  const create = useCreateProperty();
  // Maker picks either a Verkauf (sale) or Vermietung (rent) flow up
  // front. The client picker then filters on the matching client type:
  // 'prodavac' for sales, 'najmodavac' for rentals. Default = sale.
  // When a locked seller is supplied, the deal type follows that client's
  // type and the switch is hidden.
  const [dealType, setDealType] = useState<'sale' | 'rent'>(
    lockedSeller?.clientType === 'najmodavac' ? 'rent' : 'sale',
  );
  const clientTypeForDeal = dealType === 'sale' ? 'prodavac' : 'najmodavac';
  // When a seller is locked in we don't use this list (the picker is
  // hidden), but the cheap fetch is harmless and keeps the hook order stable.
  const { data: clients = [] } = useClients({ clientType: clientTypeForDeal });

  const [form, setForm] = useState<CreatePropertyPayload>(() =>
    lockedSeller ? { ...empty(), clientId: lockedSeller.id } : empty(),
  );
  const [size, setSize] = useState('');
  const [area, setArea] = useState('');
  const [price, setPrice] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const set = <K extends keyof CreatePropertyPayload>(k: K, v: CreatePropertyPayload[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);

    if (!form.clientId) {
      setErr(
        dealType === 'rent'
          ? t('properties.create.errors.landlord_required', 'Bitte einen Vermieter auswählen.')
          : t('properties.create.errors.client_required', 'Bitte einen Verkäufer auswählen.'),
      );
      return;
    }
    if (form.ediRealNumber && !EDI_REGEX.test(form.ediRealNumber)) {
      setErr('EDI Real-Nummer muss dem Format 2483/XXXXX entsprechen.');
      return;
    }

    const payload: CreatePropertyPayload = {
      clientId: form.clientId,
      city: form.city.trim() || 'Wien',
      district: form.district || null,
      street: form.street ?? null,
      streetNumber: form.streetNumber ?? null,
      postalCode: form.postalCode ?? null,
      entrance: form.entrance ?? null,
      floor: form.floor ?? null,
      doorNumber: form.doorNumber ?? null,
      buildingType: form.buildingType ?? null,
      objectType: form.objectType ?? null,
      roomsCount: form.roomsCount ?? null,
      propertySize: parseDeNumber(size),
      propertyArea: parseDeNumber(area),
      purchasePrice: (() => {
        const n = parseDeNumber(price);
        return n != null && n > 0 ? Math.round(n) : null;
      })(),
      ediRealNumber: form.ediRealNumber ?? null,
      kgGrundbuch: form.kgGrundbuch ?? null,
      einlagezahl: form.einlagezahl ?? null,
      bLaufnummer: form.bLaufnummer ?? null,
      marketingStage: form.marketingStage ?? 'lead',
    };

    try {
      const created = await create.mutateAsync(payload);
      onOpenChange(false);
      setForm(empty());
      setSize('');
      setArea('');
      setPrice('');
      onCreated?.(created.id);
    } catch (cause) {
      setErr((cause as Error).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('properties.create.title', 'Neue Immobilie anlegen')}</DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={submit}>
          {/* Geschäftsart-Schalter — entscheidet ob die Aufnahme für
              Verkauf (Verkäufer) oder Vermietung (Vermieter) angelegt wird.
              Filtert den Klient-Picker entsprechend. Bei vorausgewähltem
              Verkäufer (aus der Klienten-Detailansicht) ist die Art fix. */}
          {!lockedSeller && (
            <div className="space-y-1.5">
              <Label>{t('properties.fields.deal_type', 'Geschäftsart')} *</Label>
              <div className="inline-flex rounded-lg border border-line bg-paper-2 p-1">
                <button
                  type="button"
                  onClick={() => {
                    setDealType('sale');
                    // Reset client selection — prior pick was from a different type filter.
                    set('clientId', '');
                  }}
                  className={`rounded-md px-4 py-1.5 text-sm font-medium transition ${
                    dealType === 'sale'
                      ? 'bg-card text-ink shadow-soft'
                      : 'text-ink-3 hover:text-ink'
                  }`}
                >
                  {t('properties.fields.deal_sale', 'Verkauf')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDealType('rent');
                    set('clientId', '');
                  }}
                  className={`rounded-md px-4 py-1.5 text-sm font-medium transition ${
                    dealType === 'rent'
                      ? 'bg-card text-ink shadow-soft'
                      : 'text-ink-3 hover:text-ink'
                  }`}
                >
                  {t('properties.fields.deal_rent', 'Vermietung')}
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 col-span-2">
              <Label>
                {dealType === 'rent'
                  ? t('properties.fields.landlord', 'Vermieter')
                  : t('properties.fields.client', 'Verkäufer')}{' '}
                *
              </Label>
              {lockedSeller ? (
                <div className="flex items-center gap-2 rounded-md border border-line bg-paper-2 px-3 py-2 text-sm">
                  <UserCircle2 className="h-4 w-4 flex-shrink-0 text-ink-3" />
                  <span className="font-medium text-ink">{lockedSeller.label}</span>
                  {lockedSeller.secondary && (
                    <span className="ms-auto font-mono text-xs text-ink-3">
                      {lockedSeller.secondary}
                    </span>
                  )}
                </div>
              ) : (
                <SearchablePicker
                  value={form.clientId}
                  onChange={(v) => set('clientId', v)}
                  placeholder={
                    dealType === 'rent'
                      ? t('properties.fields.landlord_pick', 'Vermieter suchen — Name oder Telefon …')
                      : t('properties.fields.client_pick', 'Verkäufer suchen — Name oder Telefon …')
                  }
                  emptyText={t('common.no_matches', 'Keine Treffer')}
                  required
                  options={clients.map((c) => ({
                    value: c.id,
                    label: [c.title, c.first_name, c.last_name].filter(Boolean).join(' ') || '—',
                    secondary: c.phone ?? undefined,
                  }))}
                />
              )}
            </div>

            <div className="space-y-1.5 col-span-2">
              <Label>{t('properties.fields.street', 'Straße')}</Label>
              <Input
                value={form.street ?? ''}
                onChange={(e) => set('street', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('properties.fields.streetNumber', 'Hausnummer')}</Label>
              <Input
                value={form.streetNumber ?? ''}
                onChange={(e) => set('streetNumber', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('properties.fields.postalCode', 'PLZ')}</Label>
              <Input
                value={form.postalCode ?? ''}
                onChange={(e) => set('postalCode', e.target.value)}
                maxLength={4}
              />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>{t('properties.fields.city', 'Stadt')} *</Label>
              <Input value={form.city} onChange={(e) => set('city', e.target.value)} required />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>{t('properties.fields.district', 'Bezirk')}</Label>
              <Select
                value={form.district ?? ''}
                onChange={(e) => set('district', e.target.value)}
              >
                <option value="">— auswählen —</option>
                {VIENNA_DISTRICTS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>{t('properties.fields.entrance', 'Stiege')}</Label>
              <Input value={form.entrance ?? ''} onChange={(e) => set('entrance', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('properties.fields.floor', 'Stock')}</Label>
              <Input value={form.floor ?? ''} onChange={(e) => set('floor', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('properties.fields.doorNumber', 'Tür')}</Label>
              <Input
                value={form.doorNumber ?? ''}
                onChange={(e) => set('doorNumber', e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label>{t('properties.fields.buildingType', 'Gebäudeart')}</Label>
              <Select
                value={form.buildingType ?? ''}
                onChange={(e) => set('buildingType', e.target.value)}
              >
                <option value="">—</option>
                {BUILDING_TYPES.map((b) => (
                  <option key={b.value} value={b.value}>
                    {b.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t('properties.fields.objectType', 'Objektart')}</Label>
              <Select
                value={form.objectType ?? ''}
                onChange={(e) => set('objectType', e.target.value)}
              >
                <option value="">—</option>
                {OBJECT_TYPES.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t('properties.fields.rooms', 'Zimmer')}</Label>
              <Input
                value={form.roomsCount ?? ''}
                onChange={(e) => set('roomsCount', e.target.value)}
                placeholder="3,5"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('properties.fields.size', 'Wohnfläche (m²)')}</Label>
              {/* Text, not type="number": a number input hands its value over
                  with a dot decimal ("71.74") whatever the display locale, and
                  parseDeNumber read that dot as a thousand separator, so 71,74
                  m² was saved as 7174. Its step="0.1" never stopped a submit. */}
              <Input
                type="text"
                inputMode="decimal"
                value={size}
                onChange={(e) => setSize(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('properties.fields.area', 'Grundfläche (m²)')}</Label>
              <Input
                type="text"
                inputMode="decimal"
                value={area}
                onChange={(e) => setArea(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>
                {dealType === 'rent'
                  ? t('properties.fields.rent_price', 'Mietpreis (EUR/Monat)')
                  : t('properties.fields.price', 'Kaufpreis (EUR)')}
              </Label>
              <Input
                type="text"
                inputMode="numeric"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder={dealType === 'rent' ? '1.200' : '450.000'}
              />
            </div>

            <div className="col-span-2 mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 rounded-xl border border-border/60 bg-muted/30 p-3">
              <div className="space-y-1.5 col-span-2">
                <Label>EDI Real-Nummer</Label>
                <Input
                  value={form.ediRealNumber ?? ''}
                  onChange={(e) => set('ediRealNumber', e.target.value)}
                  placeholder="2483/12345"
                />
              </div>
              <div className="space-y-1.5">
                <Label>KG / Grundbuch</Label>
                <Input
                  value={form.kgGrundbuch ?? ''}
                  onChange={(e) => set('kgGrundbuch', e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Einlagezahl</Label>
                <Input
                  value={form.einlagezahl ?? ''}
                  onChange={(e) => set('einlagezahl', e.target.value)}
                />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label>B-Laufnummer</Label>
                <Input
                  value={form.bLaufnummer ?? ''}
                  onChange={(e) => set('bLaufnummer', e.target.value)}
                />
              </div>
            </div>
          </div>

          {err && (
            <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {err}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel', 'Abbrechen')}
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending
                ? t('properties.create.submitting', 'Wird angelegt …')
                : t('properties.create.submit', 'Anlegen')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
